export const STATUS_FLAGS = {
	OfficialCar: 0x001,
	Leader: 0x002,
	Lapped: 0x004,
	ClassLeader: 0x008,
	Stopped: 0x010,
	ClassLapped: 0x020,
	InPit: 0x040,
	OutLap: 0x080,
	PositionUnknown: 0x100,
	Retired: 0x200,
	Late: 0x400,
	EntryListTarget: 0x800,
	NoGpsPosition: 0x1000,
	Chequered: 0x2000,
	Hidden: 0x4000,
} as const;

export interface IRaceDetails {
	session: IRaceSession;
	rgps: IRaceRgps;
}

export interface IRaceRgps {
	gps: {
		[key: number]: {
			lat?: number;
			lon?: number;
			status?: number;
		};
	};
}

export interface IRaceSession {
	info: ISessionInfo;
	entries: ISessionEntry[];
	classes: IRaceClass[];
	bestResults: IBestResults;
	status: any;
	track: any;
}

export interface IRaceClass {
	name: string;
	order: number;
	shortName: string;
	background: string;
	foreground: string;
	lightBackground: string;
	lightForeground: string;
}
export interface ISessionInfo {
	/**
	 * If the race is endurance (multi-driver)
	 */
	champIsEndurance: boolean;

	/**
	 * Name of the championship. Ex: "FIA WEC"
	 */
	champName: string;

	/**
	 * Short name for championship
	 */
	champShortName: string;

	/**
	 * Possible values: CAR, BIKE, BOAT
	 */
	champVehicleType: 'CAR' | 'BIKE' | 'BOAT';

	/**
	 * Extra data fields (dynamic key-value pairs)
	 */
	champ_: {
		/**
		 * Other fields defined in championship
		 * Ex: "champ_ECM_Id" = "12"
		 */
		[key: string]: {
			/**
			 * The value of the extra field
			 * Note: Type is always String
			 */
			value: string;
		};
	};
	/**
	 * Session is closed in Timing System
	 */
	closed: boolean;

	/**
	 * Session date in ms since epoch time UTC
	 */
	date: number;

	/**
	 * event name
	 */
	eventName: string;

	/**
	 * event short name
	 */
	eventShortName: string;

	/**
	 * Session Name
	 */
	name: string;

	/**
	 *List of official vehicles. Key field is “transponder”
	 */
	officialVehicles: {
		name: string;
		transponder: string;
	};
}

export interface IDriver {
	/** Driver country */
	country: string;

	/** Driver first name */
	firstName: string;

	/** Driver home town */
	homeTown: string;

	/** Driver last name */
	lastName: string;

	/** Driver license */
	license: string;

	/** Driver number (order, not driver Id) */
	number: number;

	/** Driver short name */
	shortName: string;

	/** Other dynamic fields for driver */
	other: {
		/** Other fields defined in driver. Ex: "ECM_Country_Id" = "2222" */
		[key: string]: {
			/** Field name */
			name: string;

			/** Field value (always string) */
			value: string;
		};
	};
}

export interface ITransponder {
	/** Transponder code */
	code: string;
}

export interface ISessionEntry {
	/** Participant Class name */
	class: string;

	/** Current driving driver number (not driver_Id) */
	currentDriver: number;

	currentDriverFullName?: string;

	participantDetails: any;
	/** Participant division */
	division: string;

	/** List of drivers for this participant (key is driver number) */
	drivers: {
		[number: string]: IDriver;
	};

	playback: {
		url: string;
		isLive: boolean;
	};

	/** (RACE) Starting driver number (not driver_Id) */
	firstDriver: number;

	/** Participant group */
	group: string;

	/** Participant manufacturer */
	manufacturer: string;

	/** Participant manufacturer country */
	manufacturerCountry: string;

	/** Participant name */
	name: string;

	/** Participant number */
	number: string;

	/** Participant sponsor */
	sponsor: string;

	/** Participant team */
	team: string;

	/** Participant tires */
	tires: string;

	/** List of transponders (key is transponder index starting from 1) */
	transponders: {
		[index: string]: ITransponder;
	};

	/** Participant vehicle */
	vehicle: string;

	activeStanding: ITeamStanding;

	finishStanding: ITeamStanding;

	currentLapSectorData: any;
	lastLapSectorData: any;
	isActive?: boolean;
	lastLapClass: string;
	isTopSpeedOverall?: boolean;
	classDetails?: IRaceClass;
	classStanding?: IRaceClassStandings;
}

export interface IRaceClassStandings {
	atLoop: number;
	gapFirstLaps?: number;
	gapFirstTime?: number;
	gapPreviousLaps?: number;
	gapPreviousTime?: number;
	lapNumber: number;
	participant: string;
	position: number;
	positionChange: number;
	status: string;
}

export interface ITeamStanding {
	/**
	 * Sector number. Only if active and classification is By sector
	 */
	atSector?: number;

	/**
	 * Loop number. Only if active and classification is By Loop
	 */
	atLoop?: number;

	/**
	 * Laps between participant and leader
	 */
	gapFirstLaps: number;

	/**
	 * Time between participant and leader (in milliseconds)
	 */
	gapFirstTime: number;

	/**
	 * Laps between participant and previous participant
	 */
	gapPreviousLaps: number;

	/**
	 * Time between participant and previous participant (in milliseconds)
	 */
	gapPreviousTime: number;

	/**
	 * Current lap number
	 */
	lapNumber: number;

	/**
	 * Elapsed race time (in milliseconds)
	 */
	elapsedTime: number;

	/**
	 * Participant number (as string to accommodate alphanumeric values)
	 */
	participant: string;

	/**
	 * Current position in standings
	 */
	position: number;

	/**
	 * Position improvement (positive = gained positions, negative = lost positions)
	 */
	positionChange: number;

	/**
	 * Current status of the participant
	 */
	status:
		| 'CLASSIFIED'
		| 'SUBSTITUTE'
		| 'NOT_CLASSIFIED'
		| 'RETIRED'
		| 'EXCLUDED'
		| 'NOT_STARTED'
		| 'DISQUALIFIED';
}

export interface IBestResults {
	overall: {
		lap: {
			driver: number;
			lapNumber: number;
			participant: string;
			time: number;
			sectors: {
				[index: number]: {
					number: number;
					time: number;
				};
			};
		};
		sectors: {
			[index: number]: {
				driver: number;
				number: number;
				participant: string;
				time: number;
			};
		};
		topSpeed: {
			driver: number;
			participant: string;
			speed: number;
		};
	};
}

export interface ISectorData {
	[index: number]: {
		time: number;
		foreground: string;
		className: string;
	};
}
